import { createContext, useContext, useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * The single org-level funder (default ACSA). One funder per org — its abbreviation
 * is the source of truth for every funder label across the app. Stored in /settings
 * (funder_name + funder_abbr); read once here and shared via context.
 */
const FunderContext = createContext(null);

const DEFAULT_ABBR = "ACSA";

export function FunderProvider({ children }) {
  const [funder, setFunder] = useState({ abbr: DEFAULT_ABBR, name: DEFAULT_ABBR });
  const [branding, setBranding] = useState({ firmName: "", firmLogo: "" });

  const refresh = useCallback(() => {
    return api.get("/settings")
      .then(({ data }) => {
        setFunder({
          abbr: data.funder_abbr || DEFAULT_ABBR,
          name: data.funder_name || data.funder_abbr || DEFAULT_ABBR,
        });
        setBranding({ firmName: data.firm_name || "", firmLogo: data.firm_logo || "" });
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const abbr = funder.abbr;
  const value = {
    abbr,
    name: funder.name,
    firmName: branding.firmName,
    firmLogo: branding.firmLogo,
    refresh,
    /** Display label for a matter type value (stored value is unchanged). */
    typeLabel: (type) => (type === "ACSA_Client" ? `${abbr} Client` : type === "Client" ? "Client" : type),
    /** Display label for a payer value ("ACSA" -> abbreviation). */
    payerLabel: (payer) => (payer === "ACSA" ? abbr : payer),
  };

  return <FunderContext.Provider value={value}>{children}</FunderContext.Provider>;
}

export function useFunder() {
  const ctx = useContext(FunderContext);
  if (!ctx) return { abbr: DEFAULT_ABBR, name: DEFAULT_ABBR, firmName: "", firmLogo: "", refresh: () => {}, typeLabel: (t) => t, payerLabel: (p) => p };
  return ctx;
}
